import { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Loader2, Package } from 'lucide-react'
import { usePlanSearch } from '@/hooks/usePlanSearch'
import { cn } from '@/utils/cn'
import type { SearchablePlan } from '@/api/planSearch'

interface PlanSearcherProps {
  onSelect: (plan: SearchablePlan) => void
  seasonId?: string
  placeholder?: string
  autoFocus?: boolean
}

export function PlanSearcher({
  onSelect,
  seasonId,
  placeholder = 'Search...',
  autoFocus = false,
}: PlanSearcherProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    usePlanSearch({
      query,
      seasonId,
    })

  // Flatten pages to get all plans
  const plans = data?.pages.flatMap(page => page.plans) ?? []

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-focus on mount if enabled
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    // Trigger fetch when user is within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setIsOpen(true)
  }

  const handleInputFocus = () => {
    // Always open on focus to show latest plans
    setIsOpen(true)
  }

  const handleSelectPlan = (plan: SearchablePlan) => {
    onSelect(plan)
    setQuery('')
    setIsOpen(false)
  }

  // Show dropdown when open (regardless of query length)
  const showDropdown = isOpen

  // Determine empty state message
  const getEmptyMessage = () => {
    if (query.length > 0) {
      return `No plans found for "${query}"`
    }
    return 'No recent plans available'
  }

  return (
    <div
      ref={dropdownRef}
      className={cn(
        'w-full bg-white border border-[#D6DAE1] rounded-[8px]',
        'transition-all duration-200',
        showDropdown && 'rounded-b-[8px]'
      )}
    >
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#556374]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={cn(
            'w-full h-12 pl-10 pr-10 text-[14px]',
            'bg-transparent',
            'placeholder:text-[#9CA3AF]',
            'focus:outline-none',
            'transition-all duration-200'
          )}
        />
        {isLoading && !isFetchingNextPage && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#556374] animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="border-t border-[#D6DAE1] max-h-[320px] overflow-y-auto"
        >
          {/* Suggestions Header */}
          <div className="px-4 py-3">
            <span className="text-[14px] font-semibold text-[#556374]">
              {query.length > 0 ? 'Suggestions' : 'Recent Plans'}
            </span>
          </div>

          {/* Results */}
          {isLoading && !isFetchingNextPage ? (
            <div className="px-4 py-6 text-center text-[#556374]">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              <span className="text-[14px]">Searching...</span>
            </div>
          ) : plans.length > 0 ? (
            <ul className="px-2 pb-2">
              {plans.map(plan => (
                <li key={plan.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectPlan(plan)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2',
                      'hover:bg-[#f1f5f9] rounded-[6px] cursor-pointer',
                      'text-left transition-colors duration-150'
                    )}
                  >
                    {/* Icon */}
                    <Package className="h-4 w-4 text-[#334155] flex-shrink-0" />

                    {/* Plan Name */}
                    <p className="flex-1 text-[14px] font-medium text-[#1C1C1C] truncate">
                      {plan.name}
                    </p>

                    {/* Hierarchy */}
                    <p className="text-[13px] text-[#64748b] truncate">
                      {plan.hierarchy}
                    </p>
                  </button>
                </li>
              ))}
              {/* Loading indicator for infinite scroll */}
              {isFetchingNextPage && (
                <li className="px-4 py-3 text-center">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto text-[#556374]" />
                </li>
              )}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-[#556374]">
              <span className="text-[14px]">{getEmptyMessage()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
